"use client";

import React from "react";
import { Facebook, Instagram, Mail, MapPin, Phone, Loader } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
function FooterMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="space-y-2">
        {[1, 2, 3].map((_, i) => <div key={i} data-index={i} className="h-5 w-24 animate-pulse rounded-md bg-muted" />)}
      </div>;
  }
  if (!menuItems || menuItems.length === 0) {
    const fallbackLinks = [{
      label: "Početna",
      href: "/HomePage"
    }, {
      label: "Trgovina",
      href: "/ShopPage"
    }, {
      label: "Cijene",
      href: "/PricesPage"
    }];
    return <ul className="space-y-2">
        {fallbackLinks.map((item, i) => <li key={i} data-index={i}>
            <Link to={item.href} className="text-sm text-muted-foreground transition-colors hover:text-primary">
              {item.label}
            </Link>
          </li>)}
      </ul>;
  }
  return <ul className="space-y-2">
      {menuItems.map((item, i) => <li key={item.id} data-index={i}>
          <Link to={item.href || "#"} className="text-sm text-muted-foreground transition-colors hover:text-primary">
            {item.label}
          </Link>
        </li>)}
    </ul>;
}
function NewsletterForm() {
  const {
    isSubmitting,
    isSubmitted,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted) {
    return <div className="rounded-md border border-primary/20 bg-primary/10 p-4">
        <p className="text-sm font-medium text-primary">
          Uspješno ste prijavljeni na newsletter!
        </p>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-3">
      <FormField control={control} name="email" rules={{
      required: "E-mail adresa je obavezna",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Neispravna e-mail adresa"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormControl>
              <input type="email" placeholder="Vaša e-mail adresa" className="flex h-10 w-full rounded-md border border-input bg-background/50 px-3 py-2 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />
      <Button type="submit" className="w-full" disabled={isSubmitting}>
        {isSubmitting ? <>
            <Loader className="mr-2 size-4 animate-spin" />
            Prijava...
          </> : "Pretplati se"}
      </Button>
    </Form>;
}
export default function Footer() {
  return <section id="podnožje" className="dark bg-background text-foreground border-t border-border/10 pt-16 pb-8">
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
        <div className="grid grid-cols-1 gap-12 md:grid-cols-2 lg:grid-cols-4 lg:gap-8">
          {}
          <div className="space-y-6">
            <Link to="/" className="inline-block">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1576390/img/chargezone_logo_footer.png" alt="ChargeZone Logo" className="h-7 w-auto object-contain" />
            </Link>
            <p className="text-sm leading-relaxed text-muted-foreground">
              ChargeZone je specijalizirani servis i trgovina dijelova za električne romobile u Zagrebu, fokusiran na brzu obnovu baterija i kvalitetne dijelove.
            </p>
          </div>

          {}
          <div className="space-y-6">
            <h3 className="font-default text-lg font-semibold tracking-tight">
              Brzi linkovi
            </h3>
            <MenuProvider menu_id="62">
              <FooterMenuConsumer />
            </MenuProvider>
          </div>

          {}
          <div className="space-y-6">
            <h3 className="font-default text-lg font-semibold tracking-tight">
              Kontakt
            </h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-3 text-sm text-muted-foreground">
                <MapPin className="mt-0.5 size-5 shrink-0 text-primary" />
                <span>Zagreb, Hrvatska</span>
              </li>
              <li className="flex items-center gap-3 text-sm text-muted-foreground">
                <Phone className="size-5 shrink-0 text-primary" />
                <Link to="tel:+385981234567" className="transition-colors hover:text-primary">098123456789</Link>
              </li>
              <li className="flex items-center gap-3 text-sm text-muted-foreground">
                <Mail className="size-5 shrink-0 text-primary" />
                <Link to="mailto:info@chargezone.hr" className="transition-colors hover:text-primary">romobili.zg@gmail.com</Link>
              </li>
            </ul>
          </div>

          {}
          <div className="space-y-6">
            <h3 className="font-default text-lg font-semibold tracking-tight">
              Newsletter
            </h3>
            <p className="text-sm text-muted-foreground">
              Prijavite se za novosti, akcije i savjete o održavanju romobila.
            </p>
            <FormProvider formId="newsletter-footer" sectionName="footer" defaultValues={{
            email: ""
          }}>
              <NewsletterForm />
            </FormProvider>
          </div>
        </div>

        {}
        <div className="mt-16 flex flex-col items-center justify-between gap-4 border-t border-border/20 pt-8 md:flex-row">
          <p className="text-sm text-muted-foreground">
            &copy; {new Date().getFullYear()} ChargeZone. Sva prava pridržana.
          </p>
          <div className="flex items-center gap-4">
            <Link to="#" className="text-muted-foreground transition-colors hover:text-primary" aria-label="Instagram">
              <Instagram className="size-5" />
            </Link>
            <Link to="#" className="text-muted-foreground transition-colors hover:text-primary" aria-label="Facebook">
              <Facebook className="size-5" />
            </Link>
          </div>
        </div>
      </div>
    </section>;
}